// /users — the server roster (member+ nav). Members/managers see the directory;
// admins see the full roster with row actions (set server role, disable/enable,
// password-reset link, clear TOTP, delete). Managers+ can onboard new accounts.
// All actions are API-enforced; the UI surfaces LAST_ADMIN / ROLE_CEILING /
// OWNS_VAULTS / NO_PASSWORD_IDENTITY as toasts.

import { useState } from "react";
import { toast } from "sonner";
import {
  IconDots,
  IconKey,
  IconRefresh,
  IconShieldOff,
  IconTrash,
  IconUserOff,
  IconUserCheck,
  IconUserPlus,
} from "@tabler/icons-react";
import {
  cancelAccountInvite,
  clearUserTotp,
  createAccountInvite,
  errorText,
  listAccountInvites,
  listServerUsers,
  listUsers,
  passwordResetLink,
  regenerateAccountInvite,
  setUserDisabled,
  setUserRole,
  deleteUser as apiDeleteUser,
  type AdminUser,
  type PendingInvite,
  type ServerRole,
  type UserInfo,
} from "../api";
import { useApi, useUser } from "../session";
import { hasServerRole } from "../app/roles";
import { useAsyncData } from "../lib/use-async";
import { fmtDate } from "../lib/format";
import { initials } from "../app/initials";
import { RequireRole } from "../app/RequireRole";
import { PageHeader } from "@/components/page-header";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { CopyField } from "@/components/copy-field";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const SERVER_ROLES: ServerRole[] = ["admin", "manager", "member", "guest"];

export function UsersPage() {
  return (
    <RequireRole min="member">
      <UsersInner />
    </RequireRole>
  );
}

function UsersInner() {
  const api = useApi();
  const me = useUser();
  const isAdmin = hasServerRole(me.server_role, "admin");
  const isManager = hasServerRole(me.server_role, "manager");

  const roster = useAsyncData<Array<AdminUser | UserInfo>>(
    () => (isAdmin ? listServerUsers(api) : listUsers(api)),
    [isAdmin],
  );

  // Pending account invites are manager+ only; skip the fetch for members so
  // they never hit the 403 (the section is hidden for them anyway).
  const invites = useAsyncData<PendingInvite[]>(
    () => (isManager ? listAccountInvites(api) : Promise.resolve([])),
    [isManager],
  );

  const rows = roster.data ?? [];

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        eyebrow="Server directory"
        title="Users"
        actions={isManager ? <OnboardDialog onCreated={invites.reload} /> : undefined}
      />

      {roster.error && (
        <Alert variant="destructive">
          <AlertDescription>Couldn't load users: {roster.error}</AlertDescription>
        </Alert>
      )}
      {roster.loading && rows.length === 0 && (
        <div className="flex items-center gap-2 p-2 text-muted-foreground">
          <Spinner className="size-4" /> <span className="font-mono text-xs">loading…</span>
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Server role</TableHead>
                {isAdmin && <TableHead>Status</TableHead>}
                {isAdmin && <TableHead>Joined</TableHead>}
                {isAdmin && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((u) => {
                const au = isAdmin ? (u as AdminUser) : undefined;
                return (
                  <TableRow key={u.id}>
                    <TableCell>
                      <span className="flex items-center gap-2">
                        <Avatar className="size-7">
                          <AvatarFallback className="text-[0.625rem]">
                            {initials(u.display_name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{u.display_name}</span>
                        {u.id === me.id && (
                          <span className="text-xs text-muted-foreground">(you)</span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      {isAdmin ? (
                        <RolePicker userId={u.id} role={u.server_role} onChanged={roster.reload} />
                      ) : (
                        <Badge variant="secondary" className="font-mono">
                          {u.server_role}
                        </Badge>
                      )}
                    </TableCell>
                    {isAdmin && au && (
                      <TableCell>
                        {au.disabled ? (
                          <Badge variant="destructive">disabled</Badge>
                        ) : (
                          <Badge variant="secondary">active</Badge>
                        )}
                      </TableCell>
                    )}
                    {isAdmin && au && (
                      <TableCell className="text-muted-foreground">{fmtDate(au.created_at)}</TableCell>
                    )}
                    {isAdmin && au && (
                      <TableCell className="text-right">
                        <UserActions user={au} onChanged={roster.reload} />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {isManager && <PendingInvites invites={invites} />}
    </div>
  );
}

function PendingInvites({ invites }: { invites: ReturnType<typeof useAsyncData<PendingInvite[]>> }) {
  const rows = invites.data ?? [];
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold">Pending invites</h2>
        <p className="text-xs text-muted-foreground">
          Account invites that haven't been accepted yet. The invite link is shown only once and
          can't be re-displayed — regenerate one to mint a fresh link (the old link stops working),
          or cancel one to disable its link entirely.
        </p>
      </div>

      {invites.error && (
        <Alert variant="destructive">
          <AlertDescription>Couldn't load invites: {invites.error}</AlertDescription>
        </Alert>
      )}

      {invites.loading && rows.length === 0 ? (
        <div className="flex items-center gap-2 p-2 text-muted-foreground">
          <Spinner className="size-4" /> <span className="font-mono text-xs">loading…</span>
        </div>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
          No pending invites.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Invited by</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell className="font-medium">
                    {inv.email ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {inv.created_by_name ?? inv.created_by}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(inv.created_at)}</TableCell>
                  <TableCell>
                    {inv.expired ? (
                      <Badge variant="destructive">expired</Badge>
                    ) : (
                      <span className="text-muted-foreground">{fmtDate(inv.expires_at)}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <RegenerateInvite invite={inv} onRegenerated={invites.reload} />
                      <CancelInvite invite={inv} onCancelled={invites.reload} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

function RegenerateInvite({
  invite,
  onRegenerated,
}: {
  invite: PendingInvite;
  onRegenerated: () => void;
}) {
  const api = useApi();
  const [link, setLink] = useState<string | null>(null);

  async function regenerate() {
    try {
      const { invite_url } = await regenerateAccountInvite(api, invite.id);
      setLink(invite_url);
      toast.success("New invite link generated");
      // Expiry changed and the old token is dead — refresh the list.
      onRegenerated();
    } catch (e) {
      toast.error("Couldn't regenerate invite", { description: errorText(e) });
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Regenerate link"
        onClick={() => void regenerate()}
      >
        <IconRefresh />
      </Button>

      <Dialog open={link !== null} onOpenChange={(o) => !o && setLink(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New invite link</DialogTitle>
            <DialogDescription>
              {invite.email ? `Send this to ${invite.email}. ` : ""}
              This is a brand-new link (expires in 7 days) — the previous link no longer works. It's
              shown only once.
            </DialogDescription>
          </DialogHeader>
          {link && <CopyField value={link} label="Invite link" />}
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>Done</DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CancelInvite({ invite, onCancelled }: { invite: PendingInvite; onCancelled: () => void }) {
  const api = useApi();
  async function cancel() {
    try {
      await cancelAccountInvite(api, invite.id);
      toast.success("Invite cancelled");
      onCancelled();
    } catch (e) {
      toast.error("Couldn't cancel invite", { description: errorText(e) });
      onCancelled();
    }
  }
  return (
    <ConfirmDialog
      trigger={
        <Button variant="ghost" size="icon-sm" aria-label="Cancel invite">
          <IconTrash />
        </Button>
      }
      title="Cancel this invite?"
      description={
        invite.email
          ? `The invite link for ${invite.email} will stop working and can't be restored.`
          : "The invite link will stop working and can't be restored."
      }
      confirmLabel="Cancel invite"
      destructive
      onConfirm={cancel}
    />
  );
}

function RolePicker({
  userId,
  role,
  onChanged,
}: {
  userId: string;
  role: ServerRole;
  onChanged: () => void;
}) {
  const api = useApi();
  async function change(next: ServerRole) {
    if (next === role) return;
    try {
      await setUserRole(api, userId, next);
      toast.success(`Server role set to ${next}`);
      onChanged();
    } catch (e) {
      toast.error("Couldn't change role", { description: errorText(e) });
      onChanged();
    }
  }
  return (
    <Select value={role} onValueChange={(v) => void change(v as ServerRole)}>
      <SelectTrigger size="sm" className="w-32 font-mono">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {SERVER_ROLES.map((r) => (
            <SelectItem key={r} value={r} className="font-mono">
              {r}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function UserActions({ user, onChanged }: { user: AdminUser; onChanged: () => void }) {
  const api = useApi();
  const [resetLink, setResetLink] = useState<string | null>(null);

  async function toggleDisabled() {
    try {
      await setUserDisabled(api, user.id, !user.disabled);
      toast.success(user.disabled ? "Account enabled" : "Account disabled");
      onChanged();
    } catch (e) {
      toast.error("Couldn't update account", { description: errorText(e) });
    }
  }

  async function makeResetLink() {
    try {
      const { invite_url } = await passwordResetLink(api, user.id);
      setResetLink(invite_url);
    } catch (e) {
      toast.error("Couldn't create reset link", { description: errorText(e) });
    }
  }

  async function clearTotp() {
    try {
      await clearUserTotp(api, user.id);
      toast.success("Two-factor cleared");
      onChanged();
    } catch (e) {
      toast.error("Couldn't clear two-factor", { description: errorText(e) });
    }
  }

  async function remove() {
    try {
      await apiDeleteUser(api, user.id);
      toast.success(`Deleted ${user.display_name}`);
      onChanged();
    } catch (e) {
      toast.error("Couldn't delete account", { description: errorText(e) });
    }
  }

  return (
    <>
      <DropdownMenu>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="User actions"
          render={<DropdownMenuTrigger />}
        >
          <IconDots />
        </Button>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => void toggleDisabled()}>
              {user.disabled ? <IconUserCheck /> : <IconUserOff />}
              {user.disabled ? "Enable account" : "Disable account"}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void makeResetLink()}>
              <IconKey />
              Password-reset link
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void clearTotp()}>
              <IconShieldOff />
              Clear two-factor
            </DropdownMenuItem>
            <ConfirmDialog
              trigger={
                <DropdownMenuItem variant="destructive" onSelect={(e) => e.preventDefault()}>
                  <IconTrash />
                  Delete account
                </DropdownMenuItem>
              }
              title={`Delete ${user.display_name}?`}
              description="This permanently removes the account. If they still own vaults, transfer or delete those first."
              confirmLabel="Delete"
              destructive
              onConfirm={remove}
            />
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={resetLink !== null} onOpenChange={(o) => !o && setResetLink(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Password-reset link</DialogTitle>
            <DialogDescription>
              Send this to {user.display_name}. It expires in 24 hours.
            </DialogDescription>
          </DialogHeader>
          {resetLink && <CopyField value={resetLink} label="Password reset link" />}
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>Done</DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function OnboardDialog({ onCreated }: { onCreated: () => void }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function mint() {
    setBusy(true);
    setError(null);
    try {
      const res = await createAccountInvite(api, {
        ...(displayName.trim() ? { display_name_hint: displayName.trim() } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
      });
      setLink(res.invite_url);
      toast.success("Invite created");
      // Reflect the new invite in the pending-invites list immediately.
      onCreated();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        setDisplayName("");
        setEmail("");
        setLink(null);
        setError(null);
      }}
    >
      <Button render={<DialogTrigger />}>
        <IconUserPlus data-icon="inline-start" />
        Onboard account
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Onboard a new account</DialogTitle>
          <DialogDescription>
            Create an invite link for a new account. Share it, or if an email provider is configured
            it'll be emailed too.
          </DialogDescription>
        </DialogHeader>
        {link ? (
          <div className="flex flex-col gap-2">
            <span className="font-mono text-xs tracking-wider text-muted-foreground uppercase">
              Invite link (expires in 7 days)
            </span>
            <CopyField value={link} label="Invite link" />
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void mint();
            }}
          >
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="onboard-name">Display name (optional)</FieldLabel>
                <Input
                  id="onboard-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Grace Hopper"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="onboard-email">Email (optional)</FieldLabel>
                <Input
                  id="onboard-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="grace@example.com"
                />
              </Field>
            </FieldGroup>
          </form>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          {link ? (
            <DialogClose render={<Button />}>Done</DialogClose>
          ) : (
            <>
              <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
              <Button disabled={busy} onClick={() => void mint()}>
                {busy && <Spinner />}
                Create invite
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
